<template>
  <v-row wrap>
    <v-col sm="12">
      <v-card flat>
        <v-card-title>
          <v-text-field
            append-icon="search"
            :label="t('search')"
            single-line
            hide-details
            v-model="search"
            variant="underlined"
          />
        </v-card-title>
      </v-card>
      <v-card flat border>
        <v-card-text class="d-block px-0 py-0">
          <v-data-table-server
            v-if="vehicleTypesList"
            :headers="headers"
            :items="vehicleTypesList"
            :items-length="vehicleTypesListTotalCount"
            :loading="vehicleTypesListLoading"
            :search="search"
            :items-per-page-text="rowsPerPageText"
            :items-per-page-options="rowsPerPageItems"
            v-model:items-per-page="pagination.rowsPerPage"
            v-model:page="pagination.page"
            @update:options="getVehicleTypes"
            @update:sort-by="sortEvent"
          >
            <template v-slot:headers="{isSorted, getSortIcon, toggleSort }">
              <tr>
                <template v-for="header in headers" :key="header.key" >
                  <th class="custom_header_table custom-bg" :style="{width: `${header.width}px !important`}" >
                    <span class="d-flex align-center" @click="header.sortable ? toggleSort(header) : ''">
                      <span class="cursor-pointer">{{ header.title }}</span>
                      <template v-if="isSorted(header)">
                        <v-icon :icon="getSortIcon(header)" size="small"></v-icon>
                      </template>
                    </span>
                  </th>
                </template>
              </tr>
            </template>

            <template v-slot:item="{ item } : {item : any}">
              <tr>
                <td>
                  <img v-if="item.image_url" :src="item.image_url" class="vehicle-type-img" />
                </td>
                <td>
                  <router-link class="custom-a" :to="{ name: 'VehicleTypesEdit', params: { id: item.id }}">
                    {{ item.brand }}
                  </router-link>
                </td>
                <td>{{ item.model }}</td>
                <td>{{ translateVehicleTypeName(item.name) }}</td>
                <td class="text-right">
                  <v-btn v-if="auth.check('edit vehicles')" variant="text" :to="{ name: 'VehicleTypesEdit', params: { id: item.id }}" icon flat>
                    <v-icon color="grey">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn v-if="auth.check('delete vehicles')" variant="text" v-on:click="deleteVehicleType(item.id)" icon flat>
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table-server>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { datatableOptions } from '@/mixins/datatableOptions'
import Say from '@/utils/Say'
import { ref, computed, onBeforeMount } from 'vue'
import store from '@/store'
import { vehicleTypeStore } from '@/store/vehicleTypes'
import { useI18n } from 'vue-i18n'
import sayErrorResponse from '@/mixins/sayErrorResponse'
import { useVehicleTypeNames } from '@/utils/vehicleTypeNames'
import { useAuth } from '@/utils/auth'

const auth = useAuth()
const { t } = useI18n()
const { translateVehicleTypeName } = useVehicleTypeNames()

const search = ref('')
const headers: any = ref([
  { title: '', key: 'image', sortable: false, width: 10 },
  { title: t('brand'), key: 'brand', sortable: true },
  { title: t('model'), key: 'model', sortable: true },
  { title: t('type'), key: 'name', sortable: true },
  { key: '', sortable: false, width: 100 }
])
const vehicleTypesList = computed(() => vehicleTypeStore.getters.vehicleTypes)
const vehicleTypesListLoading = computed(() => vehicleTypeStore.getters.vehicleTypeListLoading)
const vehicleTypesListTotalCount = computed(() => vehicleTypeStore.getters.vehicleTypeListTotal)

const { rowsPerPageItems, rowsPerPageText, pagination } = datatableOptions()

onBeforeMount(() => {
  store.commit('app.setPageTitle', t('vehicleType', 2))
  store.commit('app.setBreadcrumbs', [
    { text: t('vehicleType', 2) }
  ])
})

const getVehicleTypes = () => {
  vehicleTypeStore.dispatch('fetchVehicleTypes', Object.assign(
    pagination.value,
    {
      search: search.value,
    }
  ))
}

const deleteVehicleType = (id: any) => {
  let confirmAction = confirm(t('vehicleTypeDeleteConfirm'))
  if (confirmAction) {
    vehicleTypeStore.dispatch('deleteVehicleType', id).then(() => {
      Say('success', t('vehicleTypeDeleteSuccess'))
    }).catch((err) => {
      sayErrorResponse(err)
    })
  }
}

const sortEvent = (value: any) => {
  if(value.length === 0) {
    return
  }
  pagination.value.sortBy = value[0].key
  pagination.value.descending = value[0].order === 'desc' ? true : false
}
</script>

<style scoped>
.vehicle-type-img {
  max-width: 48px;
  max-height: 24px;
  display: block;
  margin: -5px auto;
}
</style>
